import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { toneClass, type DsTone } from '@/ds/tone'
import { DsSkeleton } from '@/ds/skeleton'

export interface DsReceiptRow {
  label: string
  value?: ReactNode
  tone?: DsTone
  strong?: boolean
}

export interface DsReceiptProps {
  title?: string
  subtitle?: string
  status?: ReactNode
  rows?: DsReceiptRow[]
  total?: DsReceiptRow
  footer?: ReactNode
  note?: string
  card?: boolean
  loading?: boolean
  className?: string
}

export function DsReceipt({ title, subtitle, status, rows = [], total, footer, note, card = true, loading, className }: DsReceiptProps) {
  const Row = ({ label, value, tone, strong }: DsReceiptRow) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className={cn('text-small', strong ? 'text-body-strong text-foreground' : 'text-muted-foreground')}>{label}</dt>
      <dd className={cn('text-right tabular-nums', strong ? 'text-body-strong' : 'text-numeric', tone ? toneClass[tone].text : 'text-foreground')}>{value}</dd>
    </div>
  )
  return (
    <div className={cn(card && 'rounded-xl border bg-card p-4', className)}>
      {(title || status) && (
        <div className="mb-2 flex items-start justify-between gap-3">
          <div className="min-w-0">
            {title && <p className="text-title3 text-foreground">{title}</p>}
            {subtitle && <p className="text-small text-muted-foreground">{subtitle}</p>}
          </div>
          {status}
        </div>
      )}
      {loading ? (
        <div className="space-y-3 py-2">
          <DsSkeleton width="70%" height={12} />
          <DsSkeleton width="55%" height={12} />
          <DsSkeleton width="40%" height={16} />
        </div>
      ) : (
        <dl className="divide-y divide-border/60">
          {rows.map((r) => (
            <Row key={r.label} {...r} />
          ))}
          {total && (
            <div className="mt-1 border-t-2 border-border pt-1">
              <Row strong {...total} />
            </div>
          )}
        </dl>
      )}
      {note && <p className="mt-2 text-caption text-muted-foreground">{note}</p>}
      {footer && <div className="mt-3">{footer}</div>}
    </div>
  )
}
